import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import SearchBar from "./shared/SearchBar";
import "./shared/shared.css";
import "./PromptLogsPage.css";

/**
 * Logs — iteration timeline view.
 *
 * Each row is one user-initiated action (a chat message) with its full
 * downstream story: Claude's response, deploy outcome, validation errors,
 * anything auto-fix did, and so on. Expanding a row shows every event in
 * that iteration in chronological order.
 *
 * Replaces the old flat prompts-only view. If you want the raw prompt
 * list, use the type filter → "iteration_started".
 */

// Friendly labels and colors for event types.
const EVENT_META = {
  iteration_started: { label: "Prompt",     color: "#818cf8" },
  claude_response:   { label: "Response",   color: "#a78bfa" },
  deploy_result:     { label: "Deploy",     color: "#10b981" },
  error:             { label: "Error",      color: "#f87171" },
  revert_triggered:  { label: "Revert",     color: "#fbbf24" },
  snapshot_created:  { label: "Snapshot",   color: "#60a5fa" },
};

function formatTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function formatDuration(ms) {
  if (!Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 1000)}s`;
}

function truncate(str, len = 140) {
  if (!str || str.length <= len) return str;
  return str.slice(0, len) + "…";
}

function iterationSummaryLine(iter) {
  // Build a one-liner describing this iteration: prompt + outcome.
  const started = iter.events.find((e) => e.type === "iteration_started");
  const promptSnippet = started?.payload?.prompt ? truncate(started.payload.prompt, 120) : "—";
  const outcomeIcon = (() => {
    if (iter.events.some((e) => e.type === "error")) return "✗";
    if (iter.events.some((e) => e.type === "deploy_result" && e.payload?.success === true)) return "✓";
    if (iter.events.some((e) => e.type === "deploy_result" && e.payload?.success === false)) return "!";
    return "·";
  })();
  return { promptSnippet, outcomeIcon };
}

// ── Event detail renderers ─────────────────────────────────────────────

function EventDetail({ event }) {
  const p = event.payload || {};
  switch (event.type) {
    case "iteration_started":
      return (
        <div className="tl-event-body">
          <div className="tl-kv"><span className="tl-k">prompt</span><pre className="tl-v-pre">{p.prompt || "[empty]"}</pre></div>
          <div className="tl-pill-row">
            {p.isEdit && <span className="tl-pill">edit</span>}
            {p.isAutoFix && <span className="tl-pill tl-pill-warn">auto-fix</span>}
            {p.layer && <span className="tl-pill">layer: {p.layer}</span>}
            {p.attachment && <span className="tl-pill">📎 {p.attachment.filename}</span>}
            {typeof p.historyLength === "number" && <span className="tl-pill tl-pill-muted">history: {p.historyLength}</span>}
          </div>
        </div>
      );
    case "claude_response":
      return (
        <div className="tl-event-body">
          <div className="tl-pill-row">
            {p.model && <span className="tl-pill">{p.model}</span>}
            {p.layer && <span className="tl-pill">layer: {p.layer}</span>}
            {Number.isFinite(p.durationMs) && <span className="tl-pill tl-pill-muted">{formatDuration(p.durationMs)}</span>}
            {Number.isFinite(p.textBytes) && <span className="tl-pill tl-pill-muted">{p.textBytes} chars</span>}
            {Number.isFinite(p.extractedFilesCount) && p.extractedFilesCount > 0 && <span className="tl-pill">{p.extractedFilesCount} file(s)</span>}
            {p.emittedName && <span className="tl-pill tl-pill-muted">name: {p.emittedName}</span>}
          </div>
          {p.emittedSummary && (
            <div className="tl-kv"><span className="tl-k">summary</span><span className="tl-v">{p.emittedSummary}</span></div>
          )}
          {p.fullText && (
            <details className="tl-details">
              <summary>Full response ({p.fullText.length} chars)</summary>
              <pre className="tl-v-pre tl-v-scroll">{p.fullText}</pre>
            </details>
          )}
        </div>
      );
    case "deploy_result":
      return (
        <div className="tl-event-body">
          <div className="tl-pill-row">
            <span className={`tl-pill ${p.success ? "tl-pill-ok" : "tl-pill-err"}`}>
              {p.success ? "success" : "failed"}
            </span>
            {Number.isFinite(p.durationMs) && <span className="tl-pill tl-pill-muted">{formatDuration(p.durationMs)}</span>}
            {Number.isFinite(p.snapshotVersion) && <span className="tl-pill">v{p.snapshotVersion}</span>}
            {p.rejectedAtValidation && <span className="tl-pill tl-pill-err">rejected: {p.rejectedAtValidation}</span>}
            {Number.isFinite(p.fileCount) && <span className="tl-pill tl-pill-muted">{p.fileCount} file(s)</span>}
          </div>
          {p.summary && <div className="tl-kv"><span className="tl-k">summary</span><span className="tl-v">{p.summary}</span></div>}
          {p.error && <div className="tl-kv"><span className="tl-k">error</span><pre className="tl-v-pre">{p.error}</pre></div>}
          {p.file && <div className="tl-kv"><span className="tl-k">file</span><code>{p.file}</code></div>}
        </div>
      );
    case "error":
      return (
        <div className="tl-event-body">
          <div className="tl-pill-row">
            {p.source && <span className="tl-pill tl-pill-err">{p.source}</span>}
            {p.status && <span className="tl-pill tl-pill-muted">HTTP {p.status}</span>}
            {p.method && p.url && <span className="tl-pill tl-pill-muted">{p.method} {p.url}</span>}
          </div>
          {p.message && <div className="tl-kv"><span className="tl-k">message</span><pre className="tl-v-pre">{p.message}</pre></div>}
          {Array.isArray(p.details) && p.details.length > 0 && (
            <div className="tl-kv"><span className="tl-k">details</span>
              <pre className="tl-v-pre">{p.details.map((d) => `${d.path || "<root>"}: ${d.message}`).join("\n")}</pre>
            </div>
          )}
          {p.stack && (
            <details className="tl-details">
              <summary>Stack</summary>
              <pre className="tl-v-pre tl-v-scroll">{p.stack}</pre>
            </details>
          )}
        </div>
      );
    default:
      return (
        <div className="tl-event-body">
          <pre className="tl-v-pre tl-v-scroll">{JSON.stringify(p, null, 2)}</pre>
        </div>
      );
  }
}

// ── Main page ──────────────────────────────────────────────────────────

export default function PromptLogsPage() {
  const [iterations, setIterations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [appFilter, setAppFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sinceFilter, setSinceFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const fetchIterations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (userFilter) params.set("user", userFilter);
      if (appFilter) params.set("app", appFilter);
      if (sinceFilter) params.set("since", sinceFilter);
      const res = await authFetch(`${API_BASE}/telemetry/iterations?${params}`);
      const data = await res.json();
      setIterations(data.iterations || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch iterations:", err);
      setIterations([]);
    } finally {
      setLoading(false);
    }
  }, [offset, userFilter, appFilter, sinceFilter]);

  useEffect(() => { fetchIterations(); }, [fetchIterations]);

  // Client-side filtering: search (prompt text), type filter
  const filtered = iterations.filter((iter) => {
    if (typeFilter && !iter.events.some((e) => e.type === typeFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay =
        (iter.app || "").toLowerCase() +
        " " + (iter.user || "").toLowerCase() +
        " " + iter.events.map((e) =>
          (e.payload && typeof e.payload === "object" ?
            (e.payload.prompt || e.payload.fullText || e.payload.emittedSummary || e.payload.message || "") : "")
        ).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="prompt-logs-page">
      <div className="page-header">
        <h2>Logs</h2>
        <div className="page-header-right">
          <span className="apps-stat">{total} iteration{total === 1 ? "" : "s"}</span>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search prompts, responses, errors, app/user..."
          />
          <input
            type="text"
            className="prompt-logs-date-filter"
            placeholder="user (email)"
            value={userFilter}
            onChange={(e) => { setUserFilter(e.target.value); setOffset(0); }}
            style={{ width: 180 }}
          />
          <input
            type="text"
            className="prompt-logs-date-filter"
            placeholder="app"
            value={appFilter}
            onChange={(e) => { setAppFilter(e.target.value); setOffset(0); }}
            style={{ width: 160 }}
          />
          <select
            className="prompt-logs-date-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            title="Filter to iterations containing this event type"
          >
            <option value="">All events</option>
            <option value="error">With error</option>
            <option value="deploy_result">With deploy</option>
            <option value="claude_response">With Claude response</option>
            <option value="revert_triggered">With revert</option>
          </select>
          <input
            type="date"
            className="prompt-logs-date-filter"
            value={sinceFilter}
            onChange={(e) => { setSinceFilter(e.target.value); setOffset(0); }}
            title="Show iterations since this date"
          />
        </div>
        <div className="page-toolbar-right">
          <button className="prompt-logs-refresh-btn" onClick={fetchIterations} title="Refresh">↻</button>
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="page-empty">Loading timeline…</div>
        ) : filtered.length === 0 ? (
          <div className="page-empty">
            {search || userFilter || appFilter || typeFilter
              ? "No iterations match your filters."
              : "No activity yet. When users interact with the AI, iterations will appear here with full prompt / response / deploy outcome."}
          </div>
        ) : (
          <>
            <div className="tl-list">
              {filtered.map((iter) => {
                const { promptSnippet, outcomeIcon } = iterationSummaryLine(iter);
                const isOpen = expanded === iter.iterationId;
                return (
                  <div key={iter.iterationId} className={`tl-item ${isOpen ? "tl-open" : ""}`}>
                    <div
                      className="tl-row"
                      onClick={() => setExpanded(isOpen ? null : iter.iterationId)}
                    >
                      <span className={`tl-outcome tl-outcome-${outcomeIcon === "✓" ? "ok" : outcomeIcon === "✗" ? "err" : outcomeIcon === "!" ? "warn" : "neu"}`}>
                        {outcomeIcon}
                      </span>
                      <span className="tl-time">{formatTime(iter.startedAt)}</span>
                      <span className="tl-user">{iter.user || "—"}</span>
                      <span className="tl-app">{iter.app || "—"}</span>
                      <span className="tl-prompt">{promptSnippet}</span>
                      <span className="tl-count">{iter.eventCount}</span>
                      <span className="tl-caret">{isOpen ? "▾" : "▸"}</span>
                    </div>
                    {isOpen && (
                      <div className="tl-expand">
                        <div className="tl-expand-meta">
                          <span>iteration <code>{iter.iterationId}</code></span>
                          {iter.startedAt && iter.endedAt && iter.startedAt !== iter.endedAt && (
                            <span>
                              · {formatDuration(new Date(iter.endedAt) - new Date(iter.startedAt))} end-to-end
                            </span>
                          )}
                        </div>
                        {iter.events.map((ev) => {
                          const meta = EVENT_META[ev.type] || { label: ev.type, color: "#71717a" };
                          return (
                            <div key={ev.id} className="tl-event">
                              <div className="tl-event-head">
                                <span
                                  className="tl-event-badge"
                                  style={{ color: meta.color, borderColor: meta.color }}
                                >
                                  {meta.label}
                                </span>
                                <span className="tl-event-ts">{formatTime(ev.ts)}</span>
                              </div>
                              <EventDetail event={ev} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {total > limit && (
              <div className="prompt-logs-pagination">
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  Previous
                </button>
                <span className="prompt-logs-page-info">
                  {offset + 1}–{Math.min(offset + limit, total)} of {total}
                </span>
                <button
                  disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
